import { clsx } from 'clsx';
import type { SessionListItemDto } from '../lib/api.ts';
import type { ConnectionStatus } from '../lib/useSocket.ts';
import { Avatar, ChannelBadge, EmptyState, Spinner, formatRelative } from './ui.tsx';
import { ArchiveIcon, MessageSquareIcon, SearchIcon } from './icons.tsx';

export type SessionTab = 'unassigned' | 'assigned' | 'archived';

/**
 * Unread conversations per bucket — not the bucket's size.
 *
 * A badge that is always present stops being a signal; these appear only when
 * something in that tab is actually waiting to be read.
 */
export interface SessionCounts {
  unassigned: number;
  assigned: number;
  archived: number;
  /** True when a bucket filled its page, so its count is a floor, not a total. */
  capped: Record<SessionTab, boolean>;
}

interface SessionListProps {
  items: SessionListItemDto[];
  loading: boolean;
  activeUuid?: string;
  search: string;
  onSearch: (value: string) => void;
  tab: SessionTab;
  onTabChange: (tab: SessionTab) => void;
  counts: SessionCounts;
  onSelect: (uuid: string) => void;
  connectionStatus: ConnectionStatus;
  className?: string;
}

const TABS: { id: SessionTab; label: string }[] = [
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'archived', label: 'Archived' },
];

const EMPTY_COPY: Record<SessionTab, { title: string; description: string }> = {
  unassigned: {
    title: 'Nothing waiting',
    description: 'New conversations appear here the moment a customer writes in.',
  },
  assigned: {
    title: 'Nothing assigned to you',
    description: 'Pick up a conversation from Unassigned and it will move here.',
  },
  archived: {
    title: 'Nothing archived',
    description: 'Conversations you archive are kept here.',
  },
};

export function SessionList({
  items,
  loading,
  activeUuid,
  search,
  onSearch,
  tab,
  onTabChange,
  counts,
  onSelect,
  connectionStatus,
  className,
}: SessionListProps): React.JSX.Element {
  const empty = EMPTY_COPY[tab];

  return (
    <aside
      className={clsx(
        'w-full min-w-0 shrink-0 flex-col border-r border-slate-200 bg-white md:w-80 lg:w-96 dark:border-slate-800 dark:bg-slate-900',
        className,
      )}
    >
      <header className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-base font-semibold text-slate-900 dark:text-slate-50">Conversations</h1>
          <ConnectionDot status={connectionStatus} />
        </div>

        <div className="relative">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            className="lg-input pl-9"
            placeholder="Search name, email or message"
            value={search}
            onChange={(event) => {
              onSearch(event.target.value);
            }}
          />
        </div>

        <nav
          aria-label="Conversation filter"
          className="lg-scroll -mx-1 mt-3 flex gap-1 overflow-x-auto px-1"
        >
          {TABS.map((entry) => {
            const count = counts[entry.id];
            const isActive = entry.id === tab;

            return (
              <button
                key={entry.id}
                type="button"
                aria-current={isActive ? 'page' : undefined}
                onClick={() => {
                  onTabChange(entry.id);
                }}
                className={clsx(
                  'flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition',
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                )}
              >
                {entry.id === 'archived' ? <ArchiveIcon className="h-3.5 w-3.5" /> : null}
                {entry.label}

                {/* Only when something there is unread — see SessionCounts. */}
                {count > 0 ? (
                  <span
                    title={`${String(count)} unread`}
                    className="rounded-full bg-brand-600 px-1.5 py-px text-[10px] font-semibold text-white tabular-nums"
                  >
                    {count}
                    {counts.capped[entry.id] ? '+' : ''}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
      </header>

      <div className="lg-scroll min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-10 text-brand-600">
            <Spinner className="h-5 w-5" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            fill
            icon={<MessageSquareIcon className="h-10 w-10" />}
            title={search ? 'No matches' : empty.title}
            description={search ? 'Try a different name, email or phrase.' : empty.description}
          />
        ) : (
          <ul>
            {items.map((item) => (
              <SessionRow
                key={item.uuid}
                item={item}
                active={item.uuid === activeUuid}
                // The assignee is the useful detail in Assigned and Archived;
                // in Unassigned there is by definition nobody to name.
                showAssignee={tab !== 'unassigned'}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function SessionRow({
  item,
  active,
  showAssignee,
  onSelect,
}: {
  item: SessionListItemDto;
  active: boolean;
  showAssignee: boolean;
  onSelect: (uuid: string) => void;
}): React.JSX.Element {
  const preview = item.lastMessage?.content ?? 'No messages yet';

  return (
    <li>
      <button
        type="button"
        aria-current={active ? 'true' : undefined}
        onClick={() => {
          onSelect(item.uuid);
        }}
        className={clsx(
          'flex w-full items-start gap-3 border-l-2 px-4 py-3 text-left transition',
          active
            ? 'border-brand-600 bg-brand-50 dark:bg-brand-900/25'
            : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/60',
        )}
      >
        <Avatar src={item.customerAvatar} name={item.customerName} size={40} />

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span
              className={clsx(
                'truncate text-sm',
                item.unread > 0
                  ? 'font-semibold text-slate-900 dark:text-slate-50'
                  : 'font-medium text-slate-700 dark:text-slate-200',
              )}
            >
              {item.customerName}
            </span>
            <span className="shrink-0 text-[11px] text-slate-400">
              {formatRelative(item.updatedAt)}
            </span>
          </span>

          <span className="mt-0.5 flex items-center gap-2">
            <span
              className={clsx(
                'min-w-0 flex-1 truncate text-xs',
                item.unread > 0
                  ? 'text-slate-700 dark:text-slate-200'
                  : 'text-slate-500 dark:text-slate-400',
              )}
            >
              {item.lastMessage && !item.lastMessage.fromCustomer ? (
                <span className="text-slate-400">You: </span>
              ) : null}
              {preview}
            </span>

            {item.unread > 0 ? (
              <span className="lg-badge shrink-0 bg-brand-600 text-white tabular-nums">
                {item.unread > 99 ? '99+' : item.unread}
              </span>
            ) : null}
          </span>

          <span className="mt-1.5 flex items-center gap-1.5">
            <ChannelBadge type={item.type} />
            {showAssignee && item.assignedTo ? (
              <span className="truncate text-[11px] text-slate-400">{item.assignedTo.displayName}</span>
            ) : null}
          </span>
        </span>
      </button>
    </li>
  );
}

function ConnectionDot({ status }: { status: ConnectionStatus }): React.JSX.Element {
  const label =
    status === 'open' ? 'Live' : status === 'connecting' ? 'Connecting…' : 'Reconnecting…';

  return (
    <span
      className="flex items-center gap-1.5 text-[11px] text-slate-400"
      title={`Realtime connection: ${label}`}
    >
      <span
        aria-hidden="true"
        className={clsx(
          'h-1.5 w-1.5 rounded-full',
          status === 'open' ? 'bg-emerald-500' : 'animate-pulse bg-amber-500',
        )}
      />
      {label}
    </span>
  );
}
